<template>
  <nav>
    <div
      class="bb-column navigation-sidebar"
      :class="{ 'navigation-sidebar-collapsed': collapse }"
    >
      <ul class="elements bb-mt-4 bb-ml-4">
        <li
          v-for="item in items"
          :key="item.name"
          :class="item.kind === 'help-center' ? 'bb-mt-1' : 'bb-my-2'"
        >
          <button-link
            v-if="
              item.kind === 'link' ||
              (item.kind === 'accordion' && !hasIndividualSale)
            "
            v-track="item.analyticsEvent"
            :text="item.kind === 'accordion' ? 'Mis experiencias' : item.label"
            :to="item.route ?? { name: 'home' }"
            :lottie-name="item.lottie"
            :lottie-size="item.lottieSize"
            :icon="item.lottie ? undefined : item.icon"
            :hide-text="hideText"
            @click="handleNavigationClick(item)"
          />

          <div v-else-if="item.kind === 'accordion'">
            <div
              v-if="hideText"
              v-track="item.analyticsEvent"
              class="bb-cursor-pointer"
              @mouseover="playStopAnimation('play', 'folder')"
              @mouseleave="playStopAnimation('stop', 'folder')"
              @click="goTo(item.route)"
            >
              <Vue3Lottie
                ref="lottieAnimationFolder"
                :animation-data="item.lottie"
                :height="32"
                :width="32"
                :auto-play="false"
                class="lottie-icon bb-mr-4"
                @on-animation-loaded="statusLoadingLottie"
              />
            </div>

            <Accordion
              v-else
              v-track.open="item.analyticsEvent"
              class="accordion-sidebar"
              @mouseover="playStopAnimation('play', 'folder')"
              @mouseleave="playStopAnimation('stop', 'folder')"
            >
              <AccordionTab>
                <template #header>
                  <Vue3Lottie
                    ref="lottieAnimationFolder"
                    :animation-data="item.lottie"
                    :height="32"
                    :width="32"
                    :auto-play="false"
                    class="lottie-icon bb-mr-2"
                    @on-animation-loaded="statusLoadingLottie"
                  />
                  <span class="accordion-header-text bb-ml-2">
                    {{ item.label }}
                  </span>
                </template>
                <div>
                  <p
                    v-for="child in item.children"
                    :key="child.name"
                    class="accordion-header-text bb-cursor-pointer bb-mb-5"
                    @click="goTo(child.route)"
                  >
                    {{ child.label }}
                  </p>
                </div>
              </AccordionTab>
            </Accordion>
          </div>

          <div v-else-if="item.kind === 'help-center'">
            <div
              v-if="hideText"
              v-track="item.analyticsEvent"
              class="bb-cursor-pointer"
              @mouseover="playStopAnimation('play', 'chat')"
              @mouseleave="playStopAnimation('stop', 'chat')"
            >
              <Vue3Lottie
                ref="lottieAnimationChat"
                :animation-data="item.lottie"
                :height="32"
                :width="32"
                :auto-play="false"
                class="lottie-icon bb-mr-4"
                @on-animation-loaded="statusLoadingLottie"
              />
            </div>

            <Accordion
              v-else
              v-track.open="item.analyticsEvent"
              class="accordion-sidebar"
              @mouseover="playStopAnimation('play', 'chat')"
              @mouseleave="playStopAnimation('stop', 'chat')"
            >
              <AccordionTab>
                <template #header>
                  <Vue3Lottie
                    ref="lottieAnimationChat"
                    :animation-data="item.lottie"
                    :height="32"
                    :width="32"
                    :auto-play="false"
                    class="lottie-icon bb-mr-2"
                    @on-animation-loaded="statusLoadingLottie"
                  />
                  <span class="accordion-header-text bb-ml-2">
                    {{ item.label }}
                  </span>
                </template>
                <div class="help-center-text">
                  <p class="m-0">
                    Si {{ translate('necesitás') }} actualizar
                    <strong>tarifas</strong> o {{ translate('querés') }} conocer
                    <strong>nuevas propuestas</strong>, contáctate con alguno de
                    nuestros ejecutivos:
                    <span class="email-text">{{ email }}</span>
                  </p>
                  <p class="bb-pt-2">
                    Para otro tipo de consultas {{ translate('ingresá') }} al
                    <a
                      class="email-text"
                      href="https://ayuda.partners.bigbox.com.ar/lang/es_ar/"
                      target="_blank"
                    >
                      centro de ayuda
                    </a>
                  </p>
                </div>
              </AccordionTab>
            </Accordion>
          </div>
        </li>
      </ul>
      <div
        class="collapse-button bb-row bb-justify-content-center"
        :class="{ 'collapse-button-collapsed': collapse }"
        @click="collapseSidebar"
      >
        <ArrowIcon class="arrow-icon" />
      </div>
      <span class="app-version">{{ appVersion }}</span>
    </div>
  </nav>
</template>
<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';

  import ButtonLink from '@/components/Navigation/ButtonLink/ButtonLink.vue';
  import Accordion from 'primevue/accordion';
  import AccordionTab from 'primevue/accordiontab';

  import ArrowIcon from '@/assets/icons/Arrow.svg';
  import { Vue3Lottie } from 'vue3-lottie';
  import translate from '@/helpers/translate';

  import { usePartnerInfoStore } from '@/stores/partnerInfoStore';
  import { useRouter, type RouteLocationRaw } from 'vue-router';
  import { useNavigation } from '@/navigation/useNavigation';
  import type { NavItem } from '@/navigation/navigation.config';
  import { useReservationConfigurationStore } from '@/stores/reservationConfigurationStore';

  import { TLD } from '@/helpers/getLastTld';

  const router = useRouter();
  const partnerInfoStore = usePartnerInfoStore();
  const { items } = useNavigation();

  const reservationConfigurationStore = useReservationConfigurationStore();
  const hideText = ref(false);
  const appVersion = `v${__APP_VERSION__}`;
  const collapse = ref(false);

  const email = computed(() => {
    switch (TLD) {
      case 'ar':
        return 'consultas@bigbox.com.ar';
      case 'uy':
        return 'consultas@bigbox.com.uy';
      case 'mx':
        return 'consultas@bigbox.com.mx';
      case 'pe':
        return 'consultas@bigbox.com.pe';
      case 'cl':
        return 'consultas@bigbox.cl';
      case 'es':
        return 'consultas@bigbox.es';
      default:
        return '';
    }
  });

  onMounted(() => {
    partnerInfoStore.getAllPartnerActivities();
  });

  function collapseSidebar() {
    collapse.value = !collapse.value;
    hideText.value = !hideText.value;
  }

  const loadingLotties = ref(false);

  const lottieAnimationFolder = ref<InstanceType<typeof Vue3Lottie> | null>(
    null,
  );
  const lottieAnimationChat = ref<InstanceType<typeof Vue3Lottie> | null>(null);

  function playStopAnimation(
    status: 'play' | 'stop',
    animationType: 'folder' | 'chat',
  ): void {
    if (loadingLotties.value) {
      const animation =
        animationType === 'folder'
          ? lottieAnimationFolder.value
          : lottieAnimationChat.value;
      if (
        animation &&
        typeof animation.play === 'function' &&
        typeof animation.stop === 'function'
      ) {
        status === 'play' ? animation.play() : animation.stop();
      }
    }
  }
  function statusLoadingLottie() {
    loadingLotties.value = true;
  }

  function goTo(route?: RouteLocationRaw) {
    if (route) router.push(route);
  }

  const hasIndividualSale = computed((): boolean => {
    return Boolean(
      partnerInfoStore.partnerActivities?.some(activity =>
        Boolean(activity.sales_type),
      ),
    );
  });

  function handleNavigationClick(item: NavItem) {
    if (item.name === 'reservation-configuration') {
      reservationConfigurationStore.clearLastReservationLocationsViewLocationId();
    }
  }
</script>
<style lang="scss" scoped>
  /* 🎨 Forzar solo el texto del header a negro */
  ::v-deep(.accordion-sidebar .accordion-header-text) {
    width: 140px;
    color: #000000;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    font-family: 'Figtree', sans-serif;
    transition: color 0.3s ease;
  }

  /* 🎨 Hover effect para el texto del accordion */
  ::v-deep(.accordion-sidebar .accordion-header-text:hover) {
    color: $PRIMARY;
  }

  .arrow-icon {
    padding-top: 2px;
    width: 12px;
    height: auto;
    text-align: center;
    transform: rotate(90deg);
    color: $DARK-GRAY;
  }

  .navigation-sidebar {
    background-color: $WHITE;
    justify-content: space-between;
    z-index: 2;
    height: calc(100vh - 70px);
    max-width: 275px;
    position: sticky;
    top: 70px;
    left: 0;
    border-right: 1px solid $GRAY-LIGHT-3;
    &-text {
      color: $DARK-GRAY;
      font-weight: 500;
      display: inline-block;
      white-space: nowrap;
      visibility: visible;
      opacity: 1;
      transition: all 0.6s ease;
      padding-right: 24px;
    }

    &-collapsed {
      max-width: 70px;
      transition: all 0.6s ease;

      .navigation-sidebar-text {
        visibility: hidden;
        opacity: 0;
        transition: all 0.3s ease;
      }
    }

    &-element {
      :deep(svg) {
        path {
          transition: stroke 0.3s ease;
        }
        ellipse {
          transition: stroke 0.3s ease;
          stroke: $DARK-GRAY;
        }
      }
    }

    &-icon {
      :deep(svg) {
        width: 22px;
        height: 22px;
        text-align: center;
        transition: stroke 0.3s ease;
        path {
          stroke: $DARK-GRAY;
          transition: stroke 0.3s ease;
        }
      }
    }

    &-element:hover {
      .navigation-sidebar-text {
        color: $PRIMARY;
      }
      :deep(svg) {
        path {
          transition: stroke 0.3s ease;
          stroke: revert-layer;
        }
        ellipse {
          transition: stroke 0.3s ease;
          stroke: revert-layer;
        }
      }
    }

    .accordion-sidebar {
      width: min-content;
    }

    :deep(.p-accordion-content) {
      padding-right: 0;
    }
    :deep(.p-accordion-header-link) {
      padding: 0 !important;
    }
    :deep(.p-accordion-header-link:hover) {
      background-color: transparent !important;
      padding: 0 !important;
    }
    :deep(.p-accordion-header-link:focus) {
      outline: none !important;
      box-shadow: none !important;
      padding: 0 !important;
    }
    .email-text {
      color: $PRIMARY;
      text-decoration: underline;
    }
  }

  .lottie-icon {
    :deep(svg) {
      path {
        stroke: $DARK-GRAY;
      }
    }
  }

  .elements {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-around;
  }

  .app-version {
    position: absolute;
    bottom: 8px;
    left: 0;
    width: 100%;
    text-align: center;
    font-size: 12px;
    font-weight: 500;
    color: $GRAY-LIGHT-2;
  }

  .collapse-button {
    position: absolute;
    z-index: 30;
    top: 25px;
    right: -11px;
    width: 24px;
    height: 24px;
    background-color: $WHITE;
    border-radius: 50%;
    border: 1px solid $GRAY-LIGHT-3;
    box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transform: rotate(0deg);
    transition: all 0.3s ease;

    &-collapsed {
      right: -13px;
      transform: rotate(180deg);
      cursor: pointer;
    }
  }
  .button-link {
    position: relative;
    cursor: pointer;

    &-text {
      max-height: 15px;
      width: 190px;
      display: inline-block;
      white-space: nowrap;
      visibility: visible;
      opacity: 1;
      transition: all 0.3s ease;
      padding-right: 24px;
    }
    &-icon {
      padding-top: 4px;
    }
    &-new {
      background-color: $PRIMARY;
      color: $WHITE;
      border-radius: 4px;
      padding: 4px;
      font-size: 12px;
      font-weight: 500;
    }
    &-container:hover {
      .button-link-text {
        color: $PRIMARY;
        transition: color 0.3s ease;
      }
    }
    .--hide {
      width: 0px;
      padding-right: 0;
      visibility: hidden;
      opacity: 0;
      transition: all 0.3s ease;
    }
  }
  .help-center-text {
    overflow: auto;
  }
</style>
